import { useEffect, useState } from "react";
import { Grid, Container, Typography, TextField, Stack, MenuItem, Button, Box, Card, Chip, Divider, LinearProgress, Alert, IconButton, Tooltip, Fade, Zoom, Menu, ListItemIcon, ListItemText } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { AppWidgetSummary } from "../../sections/@dashboard/app";
import { RevenueApi } from "../../services/AllServices";
import Iconify from "../../components/iconify";
import { fCurrency, fPercent } from "../../utils/formatNumber";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const periodOptions = ["Daily", "Weekly", "Monthly", "Season"];

export default function RevenueManagementList() {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({});
  const [filters, setFilters] = useState({
    from: "",
    to: "",
    period: "",
  });
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);

  const handleChange = (field) => (e) => {
    setFilters((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const clearAllFilters = () => {
    setFilters({
      from: "",
      to: "",
      period: "",
    });
  };

  const setQuickFilter = (period) => {
    const today = new Date();
    let fromDate, toDate;

    switch (period) {
      case 'today':
        fromDate = toDate = today.toISOString().split('T')[0];
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        fromDate = toDate = yesterday.toISOString().split('T')[0];
        break;
      case 'last7days':
        const last7Days = new Date(today);
        last7Days.setDate(last7Days.getDate() - 7);
        fromDate = last7Days.toISOString().split('T')[0];
        toDate = today.toISOString().split('T')[0];
        break;
      case 'last30days':
        const last30Days = new Date(today);
        last30Days.setDate(last30Days.getDate() - 30);
        fromDate = last30Days.toISOString().split('T')[0];
        toDate = today.toISOString().split('T')[0];
        break;
      case 'thisMonth':
        fromDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        toDate = today.toISOString().split('T')[0];
        break;
      case 'lastMonth':
        const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        fromDate = firstDayLastMonth.toISOString().split('T')[0];
        toDate = lastDayLastMonth.toISOString().split('T')[0];
        break;
      default:
        return;
    }

    setFilters({
      from: fromDate,
      to: toDate,
      period: period === 'today' || period === 'yesterday' ? 'Daily' : 
             period === 'last7days' ? 'Weekly' : 
             period === 'last30days' || period === 'thisMonth' || period === 'lastMonth' ? 'Monthly' : '',
    });
  };

  const mapPeriodToGameDuration = (period) => {
    switch (period.toLowerCase()) {
      case "daily":
        return "daily";
      case "weekly":
        return "weekly";
      case "monthly":
        return "monthly";
      case "season":
        return "season";
      default:
        return "";
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    const payload = {
      gameDuration: mapPeriodToGameDuration(filters.period) || "",
      startDate: filters.from || "",
      endDate: filters.to || "",
    };

    try {
      const res = await RevenueApi.getTotalRevenue(payload);
      // Directly pick the summary object from API
      setDashboardData(res?.data?.data?.summary || {});
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Error fetching dashboard data", err);
      setError("Failed to fetch revenue data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Export functions
  const exportToCSV = () => {
    const csvData = [
      ['Metric', 'Value'],
      ['Total Users', (dashboardData?.totalUsers ?? 0) || 0],
      ['Total Contests', (dashboardData?.totalContests ?? 0) || 0],
      ['Total Entry Fees', fCurrency((dashboardData?.totalEntryFees ?? 0) || 0)],
      ['Total Revenue', fCurrency((dashboardData?.totalRevenue ?? 0) || 0)],
      ['Average Commission Rate', fPercent((dashboardData?.averageCommissionRate ?? 0) || 0)],
      ['Revenue Percentage', fPercent((dashboardData?.revenuePercentage ?? 0) || 0)],
    ];

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `revenue-data-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setDownloadMenuAnchor(null);
  };

  const exportToExcel = () => {
    const worksheetData = [
      ['Revenue Management Report'],
      ['Generated on:', new Date().toLocaleString()],
      ['Period:', filters.period || 'All'],
      ['From Date:', filters.from || 'All'],
      ['To Date:', filters.to || 'All'],
      [''],
      ['Metric', 'Value'],
      ['Total Users', (dashboardData?.totalUsers ?? 0) || 0],
      ['Total Contests', (dashboardData?.totalContests ?? 0) || 0],
      ['Total Entry Fees', fCurrency((dashboardData?.totalEntryFees ?? 0) || 0)],
      ['Total Revenue', fCurrency((dashboardData?.totalRevenue ?? 0) || 0)],
      ['Average Commission Rate', fPercent((dashboardData?.averageCommissionRate ?? 0) || 0)],
      ['Revenue Percentage', fPercent((dashboardData?.revenuePercentage ?? 0) || 0)],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Revenue Report');
    
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(data, `revenue-report-${new Date().toISOString().split('T')[0]}.xlsx`);
    setDownloadMenuAnchor(null);
  };

  const handleDownloadMenuOpen = (event) => {
    setDownloadMenuAnchor(event.currentTarget);
  };

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null);
  };

  return (
    <>
      <Helmet>
        <title>Revenue Management</title>
      </Helmet>

      <Container maxWidth="xl">
        {/* Header Section */}
        <Box 
          sx={{ 
            mb: 4,
            p: 4,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
              opacity: 0.3
            }
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Iconify 
                icon="mdi:chart-line-variant" 
                width={40} 
                height={40} 
                sx={{ 
                  mr: 2, 
                  color: 'white',
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                }} 
              />
          <Typography 
            variant="h3" 
            sx={{ 
                  mb: 0,
                  color: 'white',
                  fontWeight: 'bold',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}
          >
          Revenue Management
        </Typography>
            </Box>
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'rgba(255,255,255,0.9)',
                fontWeight: 400,
                textShadow: '0 1px 2px rgba(0,0,0,0.2)'
              }}
            >
            Monitor and analyze your revenue performance across different time periods
          </Typography>
          </Box>
        </Box>

        {/* Summary Cards Section */}
        <Fade in={!loading} timeout={800}>
          <Box>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <Zoom in={!loading} timeout={600} style={{ transitionDelay: '0ms' }}>
                  <Box
                    sx={{
                      p: 3,
                      height: 180,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      boxShadow: '0 8px 32px rgba(102,126,234,0.3)',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 16px 48px rgba(102,126,234,0.4)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Iconify icon="mdi:account-group" width={32} height={32} sx={{ mr: 2, opacity: 0.9 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, opacity: 0.9 }}>
                        Total Users
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                        {((dashboardData?.totalUsers ?? 0) || 0).toLocaleString()}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Active users
                      </Typography>
                    </Box>
                  </Box>
                </Zoom>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2}>
                <Zoom in={!loading} timeout={600} style={{ transitionDelay: '100ms' }}>
                  <Box
                    sx={{
                      p: 3,
                      height: 180,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                      color: 'white',
                      boxShadow: '0 8px 32px rgba(240,147,251,0.3)',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 16px 48px rgba(240,147,251,0.4)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Iconify icon="mdi:trophy-variant" width={32} height={32} sx={{ mr: 2, opacity: 0.9 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, opacity: 0.9 }}>
                        Total Contests
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                        {((dashboardData?.totalContests ?? 0) || 0).toLocaleString()}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Active contests
                      </Typography>
                    </Box>
                  </Box>
                </Zoom>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2}>
                <Zoom in={!loading} timeout={600} style={{ transitionDelay: '200ms' }}>
                  <Box
                    sx={{
                      p: 3,
                      height: 180,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                      color: 'white',
                      boxShadow: '0 8px 32px rgba(79,172,254,0.3)',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 16px 48px rgba(79,172,254,0.4)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Iconify icon="mdi:ticket-percent" width={32} height={32} sx={{ mr: 2, opacity: 0.9 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, opacity: 0.9 }}>
                        Entry Fees
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                        {fCurrency((dashboardData?.totalEntryFees ?? 0) || 0)}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Total collected
                      </Typography>
                    </Box>
                  </Box>
                </Zoom>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2}>
                <Zoom in={!loading} timeout={600} style={{ transitionDelay: '300ms' }}>
                  <Box
                    sx={{
                      p: 3,
                      height: 180,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                      color: 'white',
                      boxShadow: '0 8px 32px rgba(67,233,123,0.3)',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 16px 48px rgba(67,233,123,0.4)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Iconify icon="mdi:currency-usd" width={32} height={32} sx={{ mr: 2, opacity: 0.9 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, opacity: 0.9 }}>
                        Total Revenue
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                        {fCurrency((dashboardData?.totalRevenue ?? 0) || 0)}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Net revenue
                      </Typography>
                    </Box>
                  </Box>
                </Zoom>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2}>
                <Zoom in={!loading} timeout={600} style={{ transitionDelay: '400ms' }}>
                  <Box
                    sx={{
                      p: 3,
                      height: 180,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                      color: 'white',
                      boxShadow: '0 8px 32px rgba(250,112,154,0.3)',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 16px 48px rgba(250,112,154,0.4)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Iconify icon="mdi:percent" width={32} height={32} sx={{ mr: 2, opacity: 0.9 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, opacity: 0.9 }}>
                        Commission Rate
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                        {fPercent((dashboardData?.averageCommissionRate ?? 0) || 0)}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Average rate
                      </Typography>
                    </Box>
                  </Box>
                </Zoom>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2}>
                <Zoom in={!loading} timeout={600} style={{ transitionDelay: '500ms' }}>
                  <Box
                    sx={{
                      p: 3,
                      height: 180,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                      color: '#2d3748',
                      boxShadow: '0 8px 32px rgba(168,237,234,0.3)',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 16px 48px rgba(168,237,234,0.4)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Iconify icon="mdi:chart-donut" width={32} height={32} sx={{ mr: 2, opacity: 0.9 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, opacity: 0.9 }}>
                        Revenue Share
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                        {fPercent((dashboardData?.revenuePercentage ?? 0) || 0)}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Revenue percentage
                      </Typography>
                    </Box>
                  </Box>
                </Zoom>
              </Grid>
            </Grid>
          </Box>
        </Fade>

        {/* Filter Section */}
        <Card 
          elevation={0} 
          sx={{ 
            p: 4, 
            mb: 4,
            background: 'linear-gradient(135deg, rgba(102,126,234,0.08) 0%, rgba(118,75,162,0.08) 100%)',
            border: '1px solid',
            borderColor: 'rgba(102,126,234,0.2)',
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'url("data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23667eea" fill-opacity="0.03"%3E%3Cpath d="M20 20c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10zm10 0c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
              opacity: 0.4
            }
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  mr: 2,
                  boxShadow: '0 4px 12px rgba(102,126,234,0.3)'
                }}
              >
                <Iconify icon="mdi:filter-variant" width={24} height={24} sx={{ color: 'white' }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
              Filter & Search
            </Typography>
          </Box>

          {/* Quick Filter Buttons */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
              Quick Filters
            </Typography>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              {[
                { key: 'today', label: 'Today', icon: 'mdi:calendar-today' },
                { key: 'yesterday', label: 'Yesterday', icon: 'mdi:calendar-minus' },
                { key: 'last7days', label: 'Last 7 Days', icon: 'mdi:calendar-week' },
                { key: 'last30days', label: 'Last 30 Days', icon: 'mdi:calendar-month' },
                { key: 'thisMonth', label: 'This Month', icon: 'mdi:calendar-current-month' },
                { key: 'lastMonth', label: 'Last Month', icon: 'mdi:calendar-arrow-left' },
              ].map((filter) => (
                <Button
                  key={filter.key}
                  variant="outlined"
                  size="small"
                  onClick={() => setQuickFilter(filter.key)}
                  disabled={loading}
                  startIcon={<Iconify icon={filter.icon} width={16} height={16} />}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 2,
                    py: 1,
                    borderColor: 'divider',
                    color: 'text.primary',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'primary.lighter',
                      color: 'white',
                    },
                    '&.Mui-disabled': {
                      borderColor: 'action.disabled',
                      color: 'action.disabled',
                    }
                  }}
                >
                  {filter.label}
                </Button>
              ))}
            </Stack>
          </Box>

          <Divider sx={{ mb: 3 }} />
          
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={2}>
            <TextField
              label="From Date"
              type="date"
              value={filters.from}
              onChange={handleChange("from")}
              InputLabelProps={{ shrink: true }}
              fullWidth
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  // backgroundColor: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }
                },
                '& .MuiInputLabel-root': {
                  color: 'white',
                  fontWeight: 600
                },
                '& .MuiInputBase-input': {
                  color: 'white',
                  fontWeight: 500
                },
                '& .MuiSvgIcon-root': {
                  color: 'white'
                }
              }}
            />
            <TextField
              label="To Date"
              type="date"
              value={filters.to}
              onChange={handleChange("to")}
              InputLabelProps={{ shrink: true }}
              fullWidth
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  // backgroundColor: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }
                },
                '& .MuiInputLabel-root': {
                  color: 'white',
                  fontWeight: 600
                },
                '& .MuiInputBase-input': {
                  color: 'white',
                  fontWeight: 500
                },
                '& .MuiSvgIcon-root': {
                  color: 'white'
                }
              }}
            />
            <TextField
              select
              label="Period"
              value={filters.period}
              onChange={handleChange("period")}
              fullWidth
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  // backgroundColor: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }
                },
                '& .MuiInputLabel-root': {
                  color: 'white',
                  fontWeight: 600
                },
                '& .MuiSelect-select': {
                  color: 'white',
                  fontWeight: 500
                },
                '& .MuiSelect-select.MuiSelect-select': {
                    color: 'white'
                },
                '& .MuiInputBase-input': {
                  color: 'white'
                },
                '& .MuiSvgIcon-root': {
                  color: 'white'
                },
                '& input': {
                  color: 'white'
                }
              }}
            >
              {periodOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              size="large"
              sx={{ 
                minWidth: 150,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 15px rgba(102,126,234,0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  boxShadow: '0 6px 20px rgba(102,126,234,0.6)',
                  transform: 'translateY(-2px)'
                },
                '&:disabled': {
                  background: 'rgba(0,0,0,0.12)',
                  boxShadow: 'none'
                }
              }}
              onClick={fetchData}
              disabled={loading}
              startIcon={<Iconify icon="mdi:refresh" width={20} height={20} />}
            >
              {loading ? 'Loading...' : 'Apply Filters'}
            </Button>
            
            <Button
              variant="outlined"
              size="large"
              sx={{ 
                minWidth: 120,
                borderColor: 'error.main',
                color: 'error.main',
                borderWidth: 2,
                '&:hover': {
                  borderColor: 'error.dark',
                  backgroundColor: 'error.lighter',
                  borderWidth: 2,
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(244,67,54,0.3)'
                },
                '&:disabled': {
                  borderColor: 'rgba(0,0,0,0.12)',
                  color: 'rgba(0,0,0,0.26)'
                }
              }}
              onClick={clearAllFilters}
              disabled={loading}
              startIcon={<Iconify icon="mdi:close-circle" width={20} height={20} />}
            >
              Clear All
            </Button>

            <Button
              variant="contained"
              size="large"
              onClick={handleDownloadMenuOpen}
              disabled={loading || !dashboardData || Object.keys(dashboardData).length === 0}
              startIcon={<Iconify icon="mdi:download" width={20} height={20} />}
              sx={{ 
                minWidth: 150,
                background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
                boxShadow: '0 4px 15px rgba(76,175,80,0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #43a047 0%, #1b5e20 100%)',
                  boxShadow: '0 6px 20px rgba(76,175,80,0.6)',
                  transform: 'translateY(-2px)'
                },
                '&:disabled': {
                  background: 'rgba(0,0,0,0.12)',
                  boxShadow: 'none'
                }
              }}
            >
              Download
            </Button>
          </Stack>

          {/* Active Filters Display */}
          {(filters.from || filters.to || filters.period) && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Active Filters:
                </Typography>
                <Button
                  size="small"
                  onClick={clearAllFilters}
                  disabled={loading}
                  startIcon={<Iconify icon="mdi:close" width={14} height={14} />}
                  sx={{ 
                    minWidth: 'auto',
                    px: 1,
                    py: 0.5,
                    fontSize: '0.75rem',
                    color: 'error.main',
                    '&:hover': {
                      backgroundColor: 'error.lighter',
                    }
                  }}
                >
                  Clear All
                </Button>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {filters.from && (
                  <Chip
                    label={`From: ${filters.from}`}
                    onDelete={() => setFilters(prev => ({ ...prev, from: "" }))}
                    color="primary"
                    variant="outlined"
                    size="small"
                    icon={<Iconify icon="mdi:calendar-start" width={14} height={14} />}
                  />
                )}
                {filters.to && (
                  <Chip
                    label={`To: ${filters.to}`}
                    onDelete={() => setFilters(prev => ({ ...prev, to: "" }))}
                    color="primary"
                    variant="outlined"
                    size="small"
                    icon={<Iconify icon="mdi:calendar-end" width={14} height={14} />}
                  />
                )}
                {filters.period && (
                  <Chip
                    label={`Period: ${filters.period}`}
                    onDelete={() => setFilters(prev => ({ ...prev, period: "" }))}
                    color="primary"
                    variant="outlined"
                    size="small"
                    icon={<Iconify icon="mdi:calendar-clock" width={14} height={14} />}
                  />
                )}
              </Stack>
            </Box>
          )}
          </Box>
        </Card>

        {/* Download Menu */}
        <Menu
          anchorEl={downloadMenuAnchor}
          open={Boolean(downloadMenuAnchor)}
          onClose={handleDownloadMenuClose}
          PaperProps={{
            elevation: 8,
            sx: {
              mt: 1,
              minWidth: 200,
              borderRadius: 2,
              '& .MuiMenuItem-root': {
                px: 2,
                py: 1.5,
              },
            },
          }}
        >
          <MenuItem onClick={exportToCSV}>
            <ListItemIcon>
              <Iconify icon="mdi:file-delimited" width={20} height={20} />
            </ListItemIcon>
            <ListItemText primary="Download CSV" secondary="Comma-separated values" />
          </MenuItem>
          <MenuItem onClick={exportToExcel}>
            <ListItemIcon>
              <Iconify icon="mdi:file-excel" width={20} height={20} />
            </ListItemIcon>
            <ListItemText primary="Download Excel" secondary="Microsoft Excel format" />
          </MenuItem>
        </Menu>

        {/* Error Alert */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3 }}
            action={
              <Button color="inherit" size="small" onClick={fetchData}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <Box sx={{ mb: 3 }}>
            <LinearProgress sx={{ borderRadius: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
              Loading revenue data...
            </Typography>
          </Box>
        )}

        {/* Last Updated Info */}
        {lastUpdated && !loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, justifyContent: 'flex-end' }}>
            <Iconify icon="mdi:clock-outline" width={16} height={16} sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              Last updated: {lastUpdated.toLocaleString()}
            </Typography>
            <Tooltip title="Refresh Data">
              <IconButton size="small" onClick={fetchData} sx={{ ml: 1 }}>
                <Iconify icon="mdi:refresh" width={16} height={16} />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* No Data Message */}
        {!loading && dashboardData && Object.keys(dashboardData).length === 0 && (
          <Alert 
            severity="info" 
            sx={{ mb: 3 }}
            icon={<Iconify icon="mdi:information" width={20} height={20} />}
          >
            No revenue data found for the selected filters. Try adjusting your date range or period.
          </Alert>
        )}


      </Container>
    </>
  );
}
